import { useLifeEvents } from '@/data/sources/lifeEvents';
import { slugOf } from '../lib/focusModes';
import { buildFocusSegments } from '../lib/segments';
import { WEEK_MS } from '../lib/time';

export const REST_SLUG = 'rest';

export interface SplitRow {
  slug: string;
  minutes: number;
  share: number;
}

export const useFocusSplit = (): SplitRow[] => {
  const { data } = useLifeEvents('week');
  const events = data?.events ?? [];
  const now = Date.now();
  const earliest = events[0]?.eventTime.getTime() ?? now;
  const start = Math.max(now - WEEK_MS, earliest);

  const segments = buildFocusSegments(events, new Date(start), new Date(now));
  const minutes = new Map<string, number>();
  for (const segment of segments) {
    const from = Math.max(segment.start.getTime(), start);
    const mins = (segment.end.getTime() - from) / 60_000;
    if (mins <= 0) continue;
    const slug = slugOf(segment.name);
    minutes.set(slug, (minutes.get(slug) ?? 0) + mins);
  }

  const elapsed = (now - start) / 60_000;
  if (elapsed <= 0) return [];

  const tracked = [...minutes.values()].reduce((sum, m) => sum + m, 0);
  const rest = Math.max(0, elapsed - tracked);

  const rows: SplitRow[] = [...minutes.entries()]
    .filter(([, m]) => m > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([slug, mins]) => ({ slug, minutes: mins, share: mins / elapsed }));

  if (rest > 0) {
    rows.push({ slug: REST_SLUG, minutes: rest, share: rest / elapsed });
  }

  return rows;
};
